import { AvatarWithCheckMark } from "@chef/components";
import { formatDate } from "@chef/helpers";

import { useCompetitionResultsData } from "../../../hooks/useCompetitionResultsData";
import { useCompetitionDateValidationData } from "../../../hooks/useCompetitionDateValidationData";

import { intl } from "./CompetitionInvitationNotification.Intl";

interface CompetitionInvitationNotificationProps {
  invitesRequired: number;
  endDate: string;
  deliveryCutoffDate: string;
  startDate: string;
  deliveriesRequired: number;
}

export const CompetitionInvitationNotification = ({
  invitesRequired,
  startDate,
  endDate,
  deliveryCutoffDate,
  deliveriesRequired,
}: CompetitionInvitationNotificationProps) => {
  const {
    competitionResults,
    current: { currentDeliveries, currentInvitations },
  } = useCompetitionResultsData({
    invitesRequired,
    deliveriesRequired,
    startDate,
    endDate,
    deliveryCutoffDate,
  });

  const { isCompetitionPeriod, formattedTimeLeft, isDeliveryGracePeriod } =
    useCompetitionDateValidationData({
      startDate,
      endDate,
      deliveryCutoffDate,
    });

  const { hasAchievedTargetDeliveries, hasAchievedTargetInvites } =
    competitionResults;

  const achievedOnlyTargetInvites =
    hasAchievedTargetInvites && !hasAchievedTargetDeliveries;

  let competitionStatus;
  const numOfFriends = deliveriesRequired - currentDeliveries;
  const cutOffDate = formatDate(deliveryCutoffDate, "dd.MM.yyyy");

  if (achievedOnlyTargetInvites) {
    competitionStatus = (
      <>
        {intl.NOW_YOU_NEED_ONLY}{" "}
        <strong>
          {intl.NUM_OF_YOUR_FRIENDS({ numOfFriends: numOfFriends })}
        </strong>{" "}
        {intl.TO_RECEIVE_A_DELIVERY_BEFORE({ cutOffDate })}
      </>
    );
  } else if (!hasAchievedTargetInvites) {
    competitionStatus =
      intl.CONTINUE_RECRUITING_FRIENDS_AND_PARTICIPAGE_IN_COMPETITION;
  }

  return (
    <>
      <h3 className="mt-6 mb-4 text-lg">
        {hasAchievedTargetInvites ? intl.GOD_JOB : intl.INVITATION_SENT}
      </h3>
      <div className="px-10">
        <h4 className="font-medium">{competitionStatus}</h4>
        {achievedOnlyTargetInvites && isCompetitionPeriod && (
          <p className="mt-6">
            <strong>{intl.TIPS} </strong>
            {intl.THE_MORE_PEOPLE_YOU_INVITE_THE_MORE_DISCOUNTS_YOU_GET}
          </p>
        )}

        <div className="flex justify-center mt-6">
          {generateAvatarWithCheckMark(invitesRequired, currentInvitations)}
        </div>
        {achievedOnlyTargetInvites &&
          isDeliveryGracePeriod &&
          !isCompetitionPeriod && (
            <p className="text-error">
              {intl.THIS_INVITATON_DOES_NOT_COUNT_TOWARDS_THE_COMPETITION}
            </p>
          )}
      </div>

      {isCompetitionPeriod && (
        <p className="mt-4">
          {intl.INVITE_MORE_FRIENDS_WITHIN}{" "}
          <span className="text-error">
            <strong> {formattedTimeLeft}</strong>
          </span>
        </p>
      )}
    </>
  );
};

const generateAvatarWithCheckMark = (
  invitesRequired: number,
  totalInvitesSent: number,
) => {
  if (!invitesRequired) {
    return null;
  }
  return [...Array(invitesRequired)].map((_, i) => {
    // Here we want to show the green avatar with check mark if the invite has been sent
    const hasSentInvite = i < totalInvitesSent;

    return (
      <AvatarWithCheckMark
        key={i}
        active={hasSentInvite}
        withCheckMark={hasSentInvite}
        width={60}
        height={60}
      />
    );
  });
};
